import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, FileText, DollarSign, Users, ShoppingCart, CreditCard, Search } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

const PurchasePaymentVoucher2 = () => {
  const formRef = useRef(null);
  const [voucherNo, setVoucherNo] = useState("");
  const [purchaseId, setPurchaseId] = useState("");
  const [purchaseBillNo, setPurchaseBillNo] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [bankAcc, setBankAcc] = useState("");
  const [checkDate, setCheckDate] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  
  const [data, setData] = useState({
    VOUCHER_NO: "",
    VOUCHER_TYPE: "Payment",
    DATE: new Date().toISOString().split('T')[0],
    ACCOUNT_NO: "",
    NAME: "",
    MOBILE: "",
    DESC_NAME: "",
    CREDIT: "",
    PAY_MODE: "Cash",
  });

  const undo = () => {
    setData({
      VOUCHER_NO: "",
      VOUCHER_TYPE: "Payment",
      DATE: new Date().toISOString().split('T')[0],
      ACCOUNT_NO: "",
      NAME: "",
      MOBILE: "",
      DESC_NAME: "",
      CREDIT: "",
      PAY_MODE: "Cash",
    });
    setPurchaseId("");
    setPurchaseBillNo("");
    setCheckNumber("");
    setBankAcc("");
    setCheckDate("");
    setDate(new Date().toISOString().split('T')[0]);
  };

  useEffect(() => {
    setData((e) => ({
      ...e,
      DATE: date,
    }));
  }, [date]);

  const saveData = () => {
    if (!purchaseId) {
      toast.error("Purchase bill required to save.");
      return;
    }

    const amount = parseFloat(data.CREDIT) || 0;
    
    if (!data.NAME) {
      toast.error("Please select an account name.");
      return;
    }
    
    if (!data.DESC_NAME) {
      toast.error("Please write some description.");
      return;
    }
    
    if (amount <= 0) {
      toast.error("Please enter a valid amount.");
      return;
    }

    // Simulate save
    toast.success("Voucher saved successfully!", {
      description: "Do you want to print it?",
      action: {
        label: "Print",
        onClick: () => console.log("Print clicked"),
      },
    });
    
    undo();
  };

  return (
    <div
      ref={formRef}
      className="w-full min-h-screen bg-gradient-to-br from-slate-50 overflow-y-auto via-blue-50 to-slate-50 p-4 md:p-6"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <Card className="border-none shadow-lg bg-gradient-to-r from-blue-600 to-blue-700">
          <div className=" p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <FileText className="size-6 text-white" />
                </div>
                <div className="">
                  <div className="text-white">Purchase Payment Voucher</div>
                  <div className="text-blue-100">
                    Manage purchase payments and voucher details
                  </div>
                </div>
              </div>
              {data.VOUCHER_NO && (
                <Badge variant="secondary" className="bg-white/20 text-white border-white/30">
                  Voucher #{data.VOUCHER_NO}
                </Badge>
              )}
            </div>
          </div>
        </Card>

        {/* Main Content - Two Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Card 1 - Voucher & Payment Details */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent border-b">
              <CardTitle className="flex items-center gap-2">
                <FileText className="size-5 text-blue-600" />
                Voucher & Payment Details
              </CardTitle>
            </CardHeader>
            <CardContent className="6">
              {/* Voucher Information */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-gray-700 font-semibold">Voucher Information</div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="voucher-no">Voucher No.</Label>
                      <button
                        type="button"
                        onClick={() => {
                          toast.info("Opening voucher search...");
                          // This would open PaymentVoucherHelp modal
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Search voucher"
                      >
                        <Search className="size-4 text-blue-600" />
                      </button>
                    </div>
                    <Input
                      id="voucher-no"
                      type="number"
                      placeholder="Auto-generated"
                      value={data.VOUCHER_NO}
                      onChange={(e) =>
                        setData({ ...data, VOUCHER_NO: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="voucher-type">Voucher Type</Label>
                    <Input
                      id="voucher-type"
                      value={data.VOUCHER_TYPE}
                      readOnly
                      className="bg-gray-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                </div>
              </div>


              {/* Payment Details */}
              <div className="space-y-4 pt-3">
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-gray-700 font-semibold">Payment Details</div>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="amount" className="flex items-center gap-2">
                      Amount <span className="text-red-500">*</span>
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">₹</span>
                      <Input
                        id="amount"
                        type="number"
                        placeholder="0.00"
                        value={data.CREDIT}
                        onChange={(e) =>
                          setData({ ...data, CREDIT: e.target.value })
                        }
                        className="pl-8 text-lg"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pay-mode">Payment Mode</Label>
                    <Select
                      value={data.PAY_MODE}
                      onValueChange={(value) =>
                        setData({ ...data, PAY_MODE: value })
                      }
                    >
                      <SelectTrigger id="pay-mode">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Cheque">Cheque</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description" className="flex items-center gap-2">
                      Description <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="description"
                      placeholder="Enter payment description..."
                      value={data.DESC_NAME}
                      onChange={(e) =>
                        setData({ ...data, DESC_NAME: e.target.value })
                      }
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Cheque Details - Conditional */}
              {data.PAY_MODE === "Cheque" && (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-5 bg-indigo-500 rounded"></div>
                      <h3 className="text-gray-700">Cheque Details</h3>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="cheque-no">Cheque No.</Label>
                        <Input
                          id="cheque-no"
                          placeholder="Enter cheque number"
                          value={checkNumber}
                          onChange={(e) => setCheckNumber(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="cheque-date">Cheque Date</Label>
                        <Input
                          id="cheque-date"
                          type="date"
                          value={checkDate}
                          onChange={(e) => setCheckDate(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="bank-name">Bank Name</Label>
                        <Input
                          id="bank-name"
                          placeholder="Enter bank name"
                          value={bankAcc}
                          onChange={(e) => setBankAcc(e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Card 2 - Party & Purchase Details */}
          <Card className="shadow-lg">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent border-b">
              <CardTitle className="flex items-center gap-2">
                <Users className="size-5 text-purple-600" />
                Party & Purchase Details
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {/* Party Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-purple-500 rounded"></div>
                  <h3 className="text-gray-700">Party Details</h3>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="account-no">Account No.</Label>
                    <Input
                      id="account-no"
                      type="number"
                      placeholder="Enter account number"
                      value={data.ACCOUNT_NO}
                      onChange={(e) =>
                        setData({ ...data, ACCOUNT_NO: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="account-name" className="flex items-center gap-2">
                        Account Name <span className="text-red-500">*</span>
                      </Label>
                      <button
                        type="button"
                        onClick={() => {
                          toast.info("Opening account search...");
                          // This would open AccountMasterHelp modal
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Search account"
                      >
                        <Search className="size-4 text-purple-600" />
                      </button>
                    </div>
                    <Input
                      id="account-name"
                      placeholder="Enter account name"
                      value={data.NAME}
                      onChange={(e) =>
                        setData({ ...data, NAME: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      placeholder="Enter mobile number"
                      value={data.MOBILE}
                      onChange={(e) =>
                        setData({ ...data, MOBILE: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>


              {/* Purchase Bill Details */}
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-5 bg-amber-500 rounded"></div>
                  <h3 className="text-gray-700">Purchase Bill Details</h3>
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="purchase-id" className="flex items-center gap-2">
                        Purchase ID <span className="text-red-500">*</span>
                      </Label>
                      <button
                        type="button"
                        onClick={() => {
                          if (data.NAME) {
                            toast.info("Opening purchase bill search...");
                            // This would open PurchaseBillHelp modal
                          } else {
                            toast.error("Please select account details first");
                          }
                        }}
                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                        title="Search purchase bill"
                      >
                        <Search className="size-4 text-amber-600" />
                      </button>
                    </div>
                    <Input
                      id="purchase-id"
                      placeholder="Enter purchase ID"
                      value={purchaseId}
                      onChange={(e) => setPurchaseId(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="purchase-bill-no">Party Purchase Bill No.</Label>
                    <Input
                      id="purchase-bill-no"
                      placeholder="Bill number"
                      value={purchaseBillNo}
                      onChange={(e) => setPurchaseBillNo(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <AlertCircle className="size-4 text-amber-600" />
                <span>Please fill all required fields marked with <span className="text-red-500">*</span></span>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={undo}
                  variant="outline"
                  size="lg"
                >
                  Clear Form
                </Button>
                <Button
                  onClick={saveData}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                  size="lg"
                >
                  {data.VOUCHER_NO ? "Modify Voucher" : "Save Voucher"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PurchasePaymentVoucher2;